// Canonical admin-membership checks sourced from site.admin / device whitelist.
// Pure, no I/O. Owner/manager short-circuit matches the repo-wide is_owner||role pattern.

const idSet = (entries) =>
    new Set((entries || []).map(e => (typeof e === 'string' ? e : e?.id)).filter(Boolean));

const groupMemberMap = (groups) => {
    const list = Array.isArray(groups) ? groups : Object.values(groups || {});
    const map = new Map();
    for (const g of list) {
        const gid = g?.id || g?.organization_group_id;
        if (gid) map.set(gid, idSet(g?.users || g?.members));
    }
    return map;
};

const inAnyGroup = (userId, groupRefs, groups) => {
    if (!userId || !(groupRefs && groupRefs.length)) return false;
    const members = groupMemberMap(groups);
    for (const ref of groupRefs) {
        const gid = typeof ref === 'string' ? ref : ref?.id;
        if (gid && members.get(gid)?.has(userId)) return true;
    }
    return false;
};

export const isOwnerOrManager = (user) =>
    !!user && (user.is_owner === true || user.role === 'owner' || user.role === 'user_manager');

// NOTE: these checks do NOT include the owner/manager bypass. For UI-visibility
// decisions, callers must combine with isOwnerOrManager(user) (owners need not appear
// in admin.users / a whitelist). Keeping the bypass caller-side mirrors the existing
// row-component pattern.
export const isSiteAdmin = (userId, site, groups) => {
    if (!userId || !site) return false;
    if (idSet(site.admin?.users).has(userId)) return true;
    return inAnyGroup(userId, site.admin?.groups, groups);
};

export const isDeviceAdmin = (userId, device, sites, groups) => {
    if (!userId || !device) return false;
    const wl = device.organization_whitelist || device.user_whitelist || {};
    if (idSet(wl.organization_users).has(userId)) return true;
    if (inAnyGroup(userId, wl.organization_groups, groups)) return true;
    const parentSiteId = device.organization_site_id;
    const parentSite = (sites || []).find(s => s.id === parentSiteId || s.organization_site_id === parentSiteId);
    return parentSite ? isSiteAdmin(userId, parentSite, groups) : false;
};

export const getUserAdminSiteIds = (userId, sites, groups) =>
    (sites || []).filter(s => isSiteAdmin(userId, s, groups)).map(s => s.id || s.organization_site_id);

export const getUserAdminDeviceIds = (userId, devices, sites, groups) =>
    (devices || []).filter(d => isDeviceAdmin(userId, d, sites, groups)).map(d => d.id || d.device_id);
